// Direct sibling imports (not the "@appsmith/wds" barrel) to avoid adding a
// new package-barrel cyclic dependency — see ci-client-cyclic-deps-check.
import { FieldLabel } from "../../FieldLabel";
import { Text } from "../../Text";
import React, { forwardRef, useLayoutEffect, useRef } from "react";
import type { ForwardedRef } from "react";
import {
  Slider as HeadlessSlider,
  SliderThumb,
  SliderTrack,
} from "react-aria-components";
import type { SliderTrackRenderProps } from "react-aria-components";

import styles from "./styles.module.css";
import type { SliderMark, SliderProps } from "./types";

interface ThumbProps {
  ariaLabel?: string;
  bubbleText: string;
  getValueText?: (value: number) => string;
  index: number;
  value: number;
}

/**
 * A single slider thumb with its value bubble.
 *
 * react-aria computes `aria-valuetext` from its number formatter and does not
 * accept an override prop, so when `getValueText` is provided (category-style
 * sliders) we set the attribute on the hidden range input after each commit.
 *
 * Revisit once react-aria-components' SliderThumb accepts a native
 * aria-valuetext override — track upstream adobe/react-spectrum for a native
 * override; this useLayoutEffect workaround can then be deleted.
 */
const Thumb = (props: ThumbProps) => {
  const { ariaLabel, bubbleText, getValueText, index, value } = props;
  const inputRef = useRef<HTMLInputElement>(null);

  useLayoutEffect(() => {
    if (getValueText && inputRef.current) {
      inputRef.current.setAttribute("aria-valuetext", getValueText(value));
    }
  });

  return (
    <SliderThumb
      aria-label={ariaLabel}
      className={styles.thumb}
      index={index}
      inputRef={inputRef}
    >
      <span aria-hidden className={styles.valueBubble}>
        <Text size="footnote">{bubbleText}</Text>
      </span>
    </SliderThumb>
  );
};

interface MarkProps {
  isInRange: boolean;
  mark: SliderMark;
  percent: number;
  showMarkLabels: boolean;
}

const Mark = (props: MarkProps) => {
  const { isInRange, mark, percent, showMarkLabels } = props;

  return (
    <span
      className={styles.mark}
      data-in-range={isInRange || undefined}
      style={{ insetInlineStart: `${percent}%` }}
    >
      <span aria-hidden className={styles.markDot} />
      {Boolean(showMarkLabels) && Boolean(mark.label) && (
        <span className={styles.markLabel}>
          <Text color="neutral" size="footnote">
            {mark.label}
          </Text>
        </span>
      )}
    </span>
  );
};

const _Slider = (props: SliderProps, ref: ForwardedRef<HTMLDivElement>) => {
  const {
    contextualHelp,
    getValueText,
    isDisabled,
    label,
    marks,
    maxValue = 100,
    minValue = 0,
    showMarkLabels = true,
    showValueMode = "hover",
    thumbLabels,
    ...rest
  } = props;

  const hasMarkLabels =
    Boolean(showMarkLabels) &&
    Boolean(marks?.some((mark) => Boolean(mark.label)));

  const renderTrackChildren = (state: SliderTrackRenderProps["state"]) => {
    const thumbCount = state.values.length;
    const lowerPercent = thumbCount > 1 ? state.getThumbPercent(0) * 100 : 0;
    const upperPercent = state.getThumbPercent(thumbCount - 1) * 100;
    const [lowerValue] = state.values;
    const upperValue = state.values[thumbCount - 1];

    return (
      <>
        <span aria-hidden className={styles.rail} />
        <span
          aria-hidden
          className={styles.fill}
          style={{
            insetInlineStart: `${lowerPercent}%`,
            inlineSize: `${upperPercent - lowerPercent}%`,
          }}
        />
        {marks?.map((mark) => {
          const clamped = Math.min(Math.max(mark.value, minValue), maxValue);
          const range = maxValue - minValue;
          const percent =
            range === 0 ? 0 : ((clamped - minValue) / range) * 100;
          const isInRange =
            thumbCount > 1
              ? mark.value >= lowerValue && mark.value <= upperValue
              : mark.value <= upperValue;

          return (
            <Mark
              isInRange={isInRange}
              key={mark.value}
              mark={mark}
              percent={percent}
              showMarkLabels={showMarkLabels}
            />
          );
        })}
        {state.values.map((thumbValue, index) => (
          <Thumb
            ariaLabel={thumbLabels?.[index]}
            bubbleText={
              getValueText?.(thumbValue) ?? state.getThumbValueLabel(index)
            }
            getValueText={getValueText}
            index={index}
            key={index}
            value={thumbValue}
          />
        ))}
      </>
    );
  };

  return (
    <HeadlessSlider
      {...rest}
      className={styles.slider}
      data-show-value={showValueMode}
      isDisabled={isDisabled}
      maxValue={maxValue}
      minValue={minValue}
      ref={ref}
    >
      <FieldLabel contextualHelp={contextualHelp} isDisabled={isDisabled}>
        {label}
      </FieldLabel>
      <SliderTrack
        className={styles.track}
        data-has-mark-labels={hasMarkLabels || undefined}
      >
        {({ state }) => renderTrackChildren(state)}
      </SliderTrack>
    </HeadlessSlider>
  );
};

export const Slider = forwardRef(_Slider);
